<template>
    <section
        data-testid="initial-goal-setup"
        class="flex min-h-0 flex-1"
        :class="
            hasGoals && !props.isGenerating
                ? 'items-stretch justify-start overflow-y-auto pb-6 pt-4 text-left'
                : 'items-center justify-center px-2 py-10 text-center min-[320px]:px-6 sm:px-10'
        ">
        <div v-if="props.isGenerating" ref="generationStatus" data-testid="initial-generation-pending" tabindex="-1" class="flex w-full max-w-lg flex-col items-center gap-4">
            <InitialGenerationProgress :generation-stage="generationStage" @ready="emit('continue-generation')" />
        </div>

        <div v-else class="flex w-full flex-col gap-6" :class="hasGoals ? 'items-stretch' : 'max-w-lg items-center'">
            <div v-if="hasGoals" class="flex flex-col gap-4">
                <p class="scaffold-body text-ink-muted">
                    {{ props.goals.length === 1 ? "First goal added" : "First goals added" }}
                </p>
                <BaseCard v-for="goal in props.goals" :key="goal.id" as="article" padding="lg" radius="lgr" class="min-w-0 shadow-elevated">
                    <p v-if="goal.target_date" class="scaffold-body mb-2 flex items-center gap-2 text-ink-muted">
                        <span class="size-2 shrink-0 rounded-full bg-setup-marker-teal" aria-hidden="true"></span>
                        <time :datetime="goal.target_date">Due {{ formatTargetDate(goal.target_date) }}</time>
                    </p>
                    <h3 class="title-body-bold text-ink">{{ goal.title }}</h3>
                    <p v-if="goal.achievement_criteria" class="body mt-2 text-ink-muted">{{ goal.achievement_criteria }}</p>
                </BaseCard>
            </div>

            <div v-else class="flex flex-col gap-2">
                <h3 class="title-section text-ink">Start with a goal</h3>
                <p class="body text-ink-muted">Talk with Nadia to define a development goal you want to work towards.</p>
            </div>

            <div class="flex w-full flex-col gap-2">
                <div class="flex w-full flex-col gap-3 sm:flex-row" :class="hasGoals ? 'sm:justify-end' : 'sm:justify-center'">
                    <BaseButton
                        v-if="hasGoals && canAddGoal"
                        data-testid="initial-goal-setup-add-goal"
                        theme="neutral"
                        class="max-w-full whitespace-normal"
                        :disabled="isPending"
                        :aria-busy="isAddingGoal"
                        @click="emit('add-goal')">
                        Add another goal
                    </BaseButton>
                    <BaseButton
                        data-testid="initial-goal-setup-primary-action"
                        theme="primary"
                        class="max-w-full whitespace-normal"
                        :disabled="isPending"
                        :aria-busy="isPending"
                        @click="handlePrimaryAction">
                        {{ isStarting ? "Getting your actions ready…" : isAddingGoal ? "Opening Nadia…" : hasGoals ? "Generate actions now" : "Set up a goal" }}
                    </BaseButton>
                </div>
                <p v-if="hasGoals && canAddGoal && laterGoalActionsAvailableOn" class="body-small text-ink-muted sm:self-end">
                    If you add another goal after generating, actions for it will be available on {{ laterGoalActionsAvailableOn }}.
                </p>
            </div>

            <BaseNotification v-if="errorMessage || goalChatErrorMessage" theme="error" :text="goalChatErrorMessage ?? errorMessage ?? ''" hide-dismiss class="w-full text-left" />
        </div>
    </section>
</template>

<script setup lang="ts">
import type { DevelopmentActionGenerationProgressStage, DevelopmentGoal } from "~vue/api/generated/development-journeys";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import InitialGenerationProgress from "~vue/pages/developmentJourneys/InitialGenerationProgress.vue";
import { formatDate } from "~vue/utils/formatDate";
import { DateTime } from "luxon";
import { computed, useTemplateRef } from "vue";

const props = defineProps<{
    goals: DevelopmentGoal[];
    currentYear: number;
    canAddGoal: boolean;
    isStarting: boolean;
    isGenerating: boolean;
    generationStage: DevelopmentActionGenerationProgressStage;
    isAddingGoal: boolean;
    errorMessage: string | null;
    goalChatErrorMessage: string | null;
    laterGoalActionsAvailableOn: string | null;
}>();

const emit = defineEmits<{
    "add-goal": [];
    "start-actions": [];
    "continue-generation": [];
}>();

const hasGoals = computed(() => props.goals.length > 0);
const isPending = computed(() => props.isStarting || props.isAddingGoal);
const generationStatus = useTemplateRef<HTMLDivElement>("generationStatus");
const formatTargetDate = (targetDate: string) => formatDate(targetDate, DateTime.fromISO(targetDate).year === props.currentYear ? "month-day-long" : "date-long");

const focusGenerationStatus = () => generationStatus.value?.focus();

defineExpose({ focusGenerationStatus });

const handlePrimaryAction = () => {
    if (hasGoals.value) {
        emit("start-actions");
        return;
    }
    emit("add-goal");
};
</script>
